import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useHistory } from "react-router-dom";

import Layout from "../../../../../../../components/Layout";
import Container from "../../../../../../../components/Container";
import Flex from "../../../../../../../components/Flex";
import Breadcrumbs from "../../../../../../../components/Breadcrumbs";
import Content from "../../../../../../../components/Content";
import LinkButton from "../../../../../../../components/LinkButton";
import ButtonList from "../../../../../../../components/ButtonList";
import DataTable from "../../../../../../../components/DataTable";
import { useMessageToast } from "../../../../../../../components/MessageProvider";

import styles from "./ViewElementTrust.module.scss";

const ViewElementTrust = () => {
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const organizationApiUrl = `${clientConfigApiDomain}/organizations`;
  const clientsApiUrl = `${clientConfigApiDomain}/clients`;
  const sourcesApiUrl = `${clientConfigApiDomain}/sources`;
  const elementTrustScoreApiUrl = `${clientConfigApiDomain}/element_trust_scores`;
  // TODO this should be element_value_scores (currently gives 404) and this functionality will be fixed/tested in a future story
  const elementUseScoreApiUrl = `${clientConfigApiDomain}/element_use_scores`;

  const history = useHistory();
  const toast = useMessageToast();

  const { id, clientId, sourceId, elementTrustId } = useParams();

  const [orgData, setOrgData] = useState({});
  const [clientData, setClientData] = useState({});
  const [sourceData, setSourceData] = useState({});
  const [elementTrustData, setElementTrustData] = useState([]);
  const [elementUseData, setElementUseData] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchData = useCallback(async () => {
    const orgResponse = await fetch(`${organizationApiUrl}/${id}/`);
    const orgData = await orgResponse.json();
    setOrgData(orgData);

    const clientResponse = await fetch(`${clientsApiUrl}/${clientId}/`);
    const clientData = await clientResponse.json();
    setClientData(clientData);

    const sourceResponse = await fetch(`${sourcesApiUrl}/${sourceId}/`);
    const sourceData = await sourceResponse.json();
    setSourceData(sourceData);

    if (elementTrustId !== undefined) {
      for (let etd of sourceData.element_trust_scores) {
        if (etd.id === Number(elementTrustId)) {
          setElementTrustData(etd);

          const elementUseResponse = await fetch(
            `${elementTrustScoreApiUrl}/${etd.id}/`
          );
          const elementUseResponseData = await elementUseResponse.json();
          setElementUseData(elementUseResponseData.element_use_scores);
        }
      }
    }

    setHasLoaded(true);
  }, [
    id,
    clientId,
    sourceId,
    elementTrustId,
    organizationApiUrl,
    clientsApiUrl,
    sourcesApiUrl,
    elementTrustScoreApiUrl
  ]);

  useEffect(() => {
    if (!hasLoaded) {
      fetchData();
    }
  }, [fetchData, hasLoaded]);

  const actionCell = ({
    cell: {
      value,
      row: { original: selectedElementTrust }
    }
  }) => {
    const onEditClick = () => {
      history.push(
        `/admin/organizations/${id}/accounts/${clientId}/sources/${sourceId}/element_trust/${elementTrustId}/element_use/${selectedElementTrust.id}/edit/`
      );
    };

    const onDeleteClick = () => {
      fetch(`${elementUseScoreApiUrl}/${selectedElementTrust.id}/`, {
        method: "delete"
      })
        .then(response => {
          if (!response.ok) throw Error("Failed to delete use.");
          toast.success(
            `Successfully deleted ${selectedElementTrust.use} score for this element.`
          );
          setElementUseData(
            elementUseData.filter(el => el !== selectedElementTrust)
          );
        })
        .catch(() => {
          toast.error(
            `Error when attempting to delete ${selectedElementTrust.use} trust score for this element.`
          );
        });
    };

    return (
      <ButtonList className={styles.icons}>
        <LinkButton icon="edit" onClick={onEditClick}>
          Edit
        </LinkButton>
        <LinkButton icon="remove" onClick={onDeleteClick}>
          Delete
        </LinkButton>
      </ButtonList>
    );
  };

  const elementUseColumns = [
    {
      Header: "Use",
      accessor: "use"
    },
    {
      Header: "Trust Score",
      accessor: "score"
    },
    {
      Header: "",
      accessor: "actions",
      disableFilters: true,
      disableSortBy: true,
      Cell: actionCell
    }
  ];

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          {hasLoaded ? (
            <Flex.Item span={12}>
              <Breadcrumbs>
                <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
                <Breadcrumbs.Item
                  render={() => <Link to="/admin">Administration</Link>}
                />
                <Breadcrumbs.Item
                  render={() => (
                    <Link to="/admin/organizations">Organizations</Link>
                  )}
                />
                <Breadcrumbs.Item
                  render={() => (
                    <Link to={`/admin/organizations/${orgData.id}`}>
                      Organization: {orgData.name}
                    </Link>
                  )}
                />
                <Breadcrumbs.Item
                  render={() => (
                    <Link
                      to={`/admin/organizations/${orgData.id}/accounts/${clientData.id}`}
                    >
                      Account: {clientData.name}
                    </Link>
                  )}
                />
                <Breadcrumbs.Item
                  render={() => (
                    <Link
                      to={`/admin/organizations/${orgData.id}/accounts/${clientData.id}/sources/${sourceData.id}`}
                    >
                      Source: {sourceData.name}
                    </Link>
                  )}
                />
                <Breadcrumbs.Item last render={() => "Element Trust Score"} />
              </Breadcrumbs>
            </Flex.Item>
          ) : null}
          {!hasLoaded ? (
            <span>Loading..</span>
          ) : (
            <Flex.Item span={12}>
              <Content>
                <h1>Element Trust Scores</h1>
              </Content>
              <div className={styles.base}>
                <div className={styles.detailsFlex}></div>
                <div className={styles.details}>
                  <div>
                    <h4>Element Name: {elementTrustData.element}</h4>
                  </div>
                  <div>
                    <h4>Trust Score: {elementTrustData.trust_score}</h4>
                  </div>
                </div>

                {["name", "address", "telecom"].includes(
                  elementTrustData.element
                ) ? (
                  <div className={styles.elementTrustScores}>
                    <h3>Element Use Trust Scores</h3>
                    <ButtonList spacing={2} style={{ margin: "1rem 0" }}>
                      <LinkButton
                        icon="add"
                        onClick={() => {
                          history.push(
                            `/admin/organizations/${id}/accounts/${clientId}/sources/${sourceId}/element_trust/${elementTrustId}/element_use/add`,
                            []
                          );
                        }}
                      >
                        Add Score For Use
                      </LinkButton>
                    </ButtonList>
                    <DataTable
                      columns={elementUseColumns}
                      data={elementUseData}
                      hasSorting
                      hasPagination
                    />
                  </div>
                ) : null}
              </div>
            </Flex.Item>
          )}
        </Flex>
      </Container>
    </Layout>
  );
};

export default ViewElementTrust;
